import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { tasksAPI, dashboardAPI, categoriesAPI, inventoryAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { formatDate } from '../lib/utils';
import { 
  PieChart, Pie, Cell, ResponsiveContainer, 
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend,
  LineChart, Line, CartesianGrid,
  AreaChart, Area
} from 'recharts';
import { Button } from '../components/ui/button';
import { 
  BarChart3, 
  PieChartIcon, 
  TrendingUp, 
  Clock,
  CheckCircle2,
  AlertTriangle,
  FileWarning,
  Users,
  Calendar,
  Loader2,
  ArrowUp,
  ArrowDown,
  Minus,
  Download,
  FileText,
  Package,
  DollarSign
} from 'lucide-react';
import Layout from '../components/Layout';
import PageSkeleton from '../components/PageSkeleton';
import { TranslatableSpan } from '../components/TranslatableText';

const COLORS = {
  reported: '#3B82F6',
  assigned: '#8B5CF6', 
  in_progress: '#EAB308',
  completed: '#22C55E',
  critical: '#EF4444',
  high: '#F97316',
  medium: '#3B82F6',
  low: '#A1A1AA'
};

const ReportsPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState(null);
  const [categories, setCategories] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [exporting, setExporting] = useState(false);
  const reportRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [tasksRes, statsRes, categoriesRes, inventoryRes] = await Promise.all([
        tasksAPI.getAll(),
        dashboardAPI.getStats(),
        categoriesAPI.getAll(),
        inventoryAPI.getAll()
      ]);
      setTasks(tasksRes.data);
      setStats(statsRes.data);
      setCategories(categoriesRes.data);
      setInventory(inventoryRes.data);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Export functions
  const exportToCSV = () => {
    setExporting(true);
    
    // Task data
    let csv = 'MAINTENANCE REPORT\n';
    csv += `Generated: ${new Date().toLocaleString()}\n\n`;
    
    csv += 'WORK ORDER SUMMARY\n';
    csv += `Total Work Orders,${tasks.length}\n`;
    csv += `Completed,${tasks.filter(t => t.status === 'completed').length}\n`;
    csv += `In Progress,${tasks.filter(t => t.status === 'in_progress').length}\n`;
    csv += `Reported,${tasks.filter(t => t.status === 'reported').length}\n`;
    csv += `Overdue,${tasks.filter(t => t.deadline && t.status !== 'completed' && new Date(t.deadline) < new Date()).length}\n\n`;
    
    csv += 'WORK ORDERS BY PRIORITY\n';
    csv += `Critical,${tasks.filter(t => t.priority === 'critical').length}\n`;
    csv += `High,${tasks.filter(t => t.priority === 'high').length}\n`;
    csv += `Medium,${tasks.filter(t => t.priority === 'medium').length}\n`;
    csv += `Low,${tasks.filter(t => t.priority === 'low').length}\n\n`;
    
    csv += 'INVENTORY SUMMARY\n';
    csv += `Total Items,${inventory.length}\n`;
    csv += `Low Stock Items,${inventory.filter(i => i.quantity <= i.min_quantity).length}\n`;
    csv += `Total Value,$${inventory.reduce((acc, i) => acc + (i.quantity * i.cost_per_unit), 0).toFixed(2)}\n\n`;
    
    csv += 'INVENTORY DETAILS\n';
    csv += 'Item Name,SKU,Quantity,Min Quantity,Unit,Cost Per Unit,Total Value,Status\n';
    inventory.forEach(item => {
      const status = item.quantity <= item.min_quantity ? 'LOW STOCK' : 'OK';
      csv += `"${item.name}",${item.sku || 'N/A'},${item.quantity},${item.min_quantity},${item.unit},$${item.cost_per_unit?.toFixed(2) || '0.00'},$${(item.quantity * (item.cost_per_unit || 0)).toFixed(2)},${status}\n`;
    });
    
    csv += '\nWORK ORDER DETAILS\n';
    csv += 'Title,Status,Priority,Assigned To,Category,Deadline,Created At\n';
    tasks.forEach(task => {
      csv += `"${task.title}",${task.status},${task.priority},"${task.assigned_to_name || 'Unassigned'}","${task.category_name || 'N/A'}",${task.deadline || 'N/A'},${task.created_at.split('T')[0]}\n`;
    });
    
    // Download - append to DOM for browser compatibility
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `maintenance_report_${new Date().toISOString().split('T')[0]}.csv`;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    
    setExporting(false);
  };

  const exportToPDF = () => {
    setExporting(true);
    
    // Create printable HTML
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Maintenance Report</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; color: #333; }
          h1 { color: #001133; border-bottom: 3px solid #C9A227; padding-bottom: 10px; }
          h2 { color: #001133; margin-top: 30px; }
          .stat-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px; margin: 20px 0; }
          .stat-box { background: #f5f5f5; padding: 15px; border-radius: 8px; text-align: center; }
          .stat-value { font-size: 28px; font-weight: bold; color: #001133; }
          .stat-label { color: #666; font-size: 12px; text-transform: uppercase; }
          table { width: 100%; border-collapse: collapse; margin: 15px 0; }
          th, td { border: 1px solid #ddd; padding: 10px; text-align: left; }
          th { background: #001133; color: white; }
          tr:nth-child(even) { background: #f9f9f9; }
          .low-stock { background: #FEE2E2; color: #DC2626; }
          .status-completed { color: #22C55E; font-weight: bold; }
          .status-in_progress { color: #EAB308; font-weight: bold; }
          .status-reported { color: #3B82F6; font-weight: bold; }
          .priority-critical { color: #EF4444; font-weight: bold; }
          .priority-high { color: #F97316; font-weight: bold; }
          .footer { margin-top: 40px; text-align: center; color: #888; font-size: 12px; }
        </style>
      </head>
      <body>
        <h1>Maintenance Report</h1>
        <p>Generated: ${new Date().toLocaleString()}</p>
        
        <h2>Work Order Summary</h2>
        <div class="stat-grid">
          <div class="stat-box">
            <div class="stat-value">${tasks.length}</div>
            <div class="stat-label">Total Work Orders</div>
          </div>
          <div class="stat-box">
            <div class="stat-value">${tasks.filter(t => t.status === 'completed').length}</div>
            <div class="stat-label">Completed</div>
          </div>
          <div class="stat-box">
            <div class="stat-value">${tasks.filter(t => t.status === 'in_progress').length}</div>
            <div class="stat-label">In Progress</div>
          </div>
          <div class="stat-box">
            <div class="stat-value">${tasks.filter(t => t.deadline && t.status !== 'completed' && new Date(t.deadline) < new Date()).length}</div>
            <div class="stat-label">Overdue</div>
          </div>
        </div>
        
        <h2>Inventory Summary</h2>
        <div class="stat-grid">
          <div class="stat-box">
            <div class="stat-value">${inventory.length}</div>
            <div class="stat-label">Total Items</div>
          </div>
          <div class="stat-box">
            <div class="stat-value">${inventory.filter(i => i.quantity <= i.min_quantity).length}</div>
            <div class="stat-label">Low Stock</div>
          </div>
          <div class="stat-box">
            <div class="stat-value">$${inventory.reduce((acc, i) => acc + (i.quantity * (i.cost_per_unit || 0)), 0).toFixed(2)}</div>
            <div class="stat-label">Total Value</div>
          </div>
          <div class="stat-box">
            <div class="stat-value">${inventory.reduce((acc, i) => acc + i.quantity, 0)}</div>
            <div class="stat-label">Total Units</div>
          </div>
        </div>
        
        <h2>Inventory Details</h2>
        <table>
          <thead>
            <tr>
              <th>Item</th>
              <th>SKU</th>
              <th>Quantity</th>
              <th>Min Qty</th>
              <th>Unit Cost</th>
              <th>Total Value</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            ${inventory.map(item => `
              <tr class="${item.quantity <= item.min_quantity ? 'low-stock' : ''}">
                <td>${item.name}</td>
                <td>${item.sku || 'N/A'}</td>
                <td>${item.quantity}</td>
                <td>${item.min_quantity}</td>
                <td>$${(item.cost_per_unit || 0).toFixed(2)}</td>
                <td>$${(item.quantity * (item.cost_per_unit || 0)).toFixed(2)}</td>
                <td>${item.quantity <= item.min_quantity ? 'LOW STOCK' : 'OK'}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        
        <h2>Recent Work Orders</h2>
        <table>
          <thead>
            <tr>
              <th>Title</th>
              <th>Status</th>
              <th>Priority</th>
              <th>Assigned To</th>
              <th>Deadline</th>
            </tr>
          </thead>
          <tbody>
            ${tasks.slice(0, 20).map(task => `
              <tr>
                <td>${task.title}</td>
                <td class="status-${task.status}">${task.status.replace('_', ' ').toUpperCase()}</td>
                <td class="priority-${task.priority}">${task.priority.toUpperCase()}</td>
                <td>${task.assigned_to_name || 'Unassigned'}</td>
                <td>${task.deadline ? new Date(task.deadline).toLocaleDateString() : 'N/A'}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        
        <div class="footer">
          <p>WorkStream Pro Maintenance Management System</p>
        </div>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    // Use DOMParser for safe HTML rendering instead of document.write
    const parser = new DOMParser();
    const doc = parser.parseFromString(printContent, 'text/html');
    printWindow.document.replaceChild(
      printWindow.document.importNode(doc.documentElement, true),
      printWindow.document.documentElement
    );
    printWindow.print();
    
    setExporting(false);
  };

  if (loading) {
    return <PageSkeleton title="Loading reports" />;
  }

  // Calculate statistics
  const totalTasks = tasks.length;
  const completedTasks = tasks.filter(t => t.status === 'completed').length;
  const pendingTasks = tasks.filter(t => t.status !== 'completed').length;
  const completionRate = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

  // Status distribution for pie chart - use tasks array if stats is not available
  const statusData = [
    { name: 'Reported', value: stats?.by_status?.reported || tasks.filter(t => t.status === 'reported').length, color: COLORS.reported },
    { name: 'Assigned', value: stats?.by_status?.assigned || tasks.filter(t => t.status === 'assigned').length, color: COLORS.assigned },
    { name: 'In Progress', value: stats?.by_status?.in_progress || tasks.filter(t => t.status === 'in_progress').length, color: COLORS.in_progress },
    { name: 'Completed', value: stats?.by_status?.completed || tasks.filter(t => t.status === 'completed').length, color: COLORS.completed },
  ].filter(d => d.value > 0);

  // Priority distribution
  const priorityData = [
    { name: 'Critical', value: tasks.filter(t => t.priority === 'critical').length, color: COLORS.critical },
    { name: 'High', value: tasks.filter(t => t.priority === 'high').length, color: COLORS.high },
    { name: 'Medium', value: tasks.filter(t => t.priority === 'medium').length, color: COLORS.medium },
    { name: 'Low', value: tasks.filter(t => t.priority === 'low').length, color: COLORS.low },
  ].filter(d => d.value > 0);

  // Tasks by category
  const categoryData = categories.map(cat => ({
    name: cat.name,
    total: tasks.filter(t => t.category_id === cat.id).length,
    completed: tasks.filter(t => t.category_id === cat.id && t.status === 'completed').length,
    pending: tasks.filter(t => t.category_id === cat.id && t.status !== 'completed').length,
    color: cat.color
  })).filter(c => c.total > 0).sort((a, b) => b.total - a.total);

  // Tasks over time (last 7 days)
  const last7Days = [];
  for (let i = 6; i >= 0; i--) {
    const date = new Date();
    date.setDate(date.getDate() - i);
    const dateStr = date.toISOString().split('T')[0];
    
    const created = tasks.filter(t => t.created_at.split('T')[0] === dateStr).length;
    const completed = tasks.filter(t => 
      t.status === 'completed' && 
      t.updated_at.split('T')[0] === dateStr
    ).length;
    
    last7Days.push({
      date: date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
      created,
      completed
    });
  }

  // Milestone progress
  const totalMilestones = tasks.reduce((acc, t) => acc + (t.milestones?.length || 0), 0);
  const completedMilestones = tasks.reduce((acc, t) => 
    acc + (t.milestones?.filter(m => m.completed).length || 0), 0
  );
  const milestoneProgress = totalMilestones > 0 ? Math.round((completedMilestones / totalMilestones) * 100) : 0;

  // Overdue tasks
  const overdueTasks = tasks.filter(t => {
    if (!t.deadline || t.status === 'completed') return false;
    return new Date(t.deadline) < new Date();
  });

  // Tasks by assignee
  const assigneeData = {};
  tasks.forEach(t => {
    const name = t.assigned_to_name || 'Unassigned';
    if (!assigneeData[name]) {
      assigneeData[name] = { total: 0, completed: 0, pending: 0 };
    }
    assigneeData[name].total++;
    if (t.status === 'completed') {
      assigneeData[name].completed++;
    } else {
      assigneeData[name].pending++;
    }
  });
  const assigneeChartData = Object.entries(assigneeData)
    .map(([name, data]) => ({ name, ...data }))
    .sort((a, b) => b.total - a.total)
    .slice(0, 10);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-slate-900 border border-slate-600 p-3 rounded-sm shadow-lg">
          <p className="text-white font-medium mb-1">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }} className="text-sm">
              {entry.name}: {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Navigation helper for clickable report elements
  const handleFilterNavigation = (filterType, filterValue) => {
    const params = new URLSearchParams();
    
    switch (filterType) {
      case 'status':
        // Map display names to API values
        const statusMap = {
          'Reported': 'reported',
          'Assigned': 'assigned', 
          'In Progress': 'in_progress',
          'Completed': 'completed'
        };
        params.set('status', statusMap[filterValue] || filterValue.toLowerCase().replace(' ', '_'));
        break;
      case 'priority':
        params.set('priority', filterValue.toLowerCase());
        break;
      case 'category':
        params.set('category', filterValue);
        break;
      case 'assignee':
        params.set('assignee', filterValue);
        break;
      case 'overdue':
        params.set('overdue', 'true');
        break;
      case 'unassigned':
        params.set('unassigned', 'true');
        break;
      default:
        break;
    }
    
    navigate(`/tasks?${params.toString()}`);
  };

  // Inventory calculations
  const lowStockItems = inventory.filter(i => i.quantity <= i.min_quantity);
  const totalInventoryValue = inventory.reduce((acc, i) => acc + (i.quantity * (i.cost_per_unit || 0)), 0);
  const totalUnits = inventory.reduce((acc, i) => acc + i.quantity, 0);
  
  // Parts used in tasks
  const totalPartsUsed = tasks.reduce((acc, t) => acc + (t.parts_used?.length || 0), 0);
  const totalPartsCost = tasks.reduce((acc, t) => 
    acc + (t.parts_used?.reduce((sum, p) => sum + (p.quantity * (p.cost_per_unit || 0)), 0) || 0), 0
  );

  return (
    <Layout>
      <div className="space-y-8" data-testid="reports-page" ref={reportRef}>
        {/* Header with Export Buttons */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold uppercase tracking-tight text-white flex items-center gap-3">
              <BarChart3 className="w-10 h-10 text-amber-500" />
              {t('reports.title')}
            </h1>
            <p className="text-slate-400 mt-1">{t('reports.subtitle')}</p>
          </div>
          <div className="flex gap-2">
            <Button 
              onClick={exportToCSV} 
              disabled={exporting}
              className="btn-secondary flex items-center gap-2"
              data-testid="export-csv-btn"
            >
              <FileText className="w-4 h-4" />
              {t('reports.exportCSV')}
            </Button>
            <Button 
              onClick={exportToPDF} 
              disabled={exporting}
              className="btn-primary flex items-center gap-2"
              data-testid="export-pdf-btn"
            >
              <Download className="w-4 h-4" />
              {t('reports.printPDF')}
            </Button>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div 
            className="card-industrial cursor-pointer hover:border-amber-500/50 transition-colors" 
            data-testid="metric-total"
            onClick={() => navigate('/tasks')}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 uppercase text-xs tracking-wider font-mono">{t('reports.totalWorkOrders')}</p>
                <p className="text-3xl font-bold text-white mt-1">{totalTasks}</p>
              </div>
              <div className="w-12 h-12 bg-slate-700 rounded-sm flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-slate-400" />
              </div>
            </div>
          </div>

          <div 
            className="card-industrial cursor-pointer hover:border-green-500/50 transition-colors" 
            data-testid="metric-completion"
            onClick={() => handleFilterNavigation('status', 'Completed')}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 uppercase text-xs tracking-wider font-mono">{t('reports.completionRate')}</p>
                <p className="text-3xl font-bold text-green-400 mt-1">{completionRate}%</p>
              </div>
              <div className="w-12 h-12 bg-green-500/20 rounded-sm flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-green-400" />
              </div>
            </div>
            <div className="mt-3 h-2 bg-slate-700 rounded-full overflow-hidden">
              <div 
                className="h-full bg-green-500 transition-all duration-500"
                style={{ width: `${completionRate}%` }}
              />
            </div>
          </div>

          <div 
            className="card-industrial cursor-pointer hover:border-yellow-500/50 transition-colors" 
            data-testid="metric-pending"
            onClick={() => navigate('/tasks?status=in_progress')}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 uppercase text-xs tracking-wider font-mono">{t('reports.pending')}</p>
                <p className="text-3xl font-bold text-yellow-400 mt-1">{pendingTasks}</p>
              </div>
              <div className="w-12 h-12 bg-yellow-500/20 rounded-sm flex items-center justify-center">
                <Clock className="w-6 h-6 text-yellow-400" />
              </div>
            </div>
          </div>

          <div 
            className="card-industrial cursor-pointer hover:border-red-500/50 transition-colors" 
            data-testid="metric-overdue"
            onClick={() => handleFilterNavigation('overdue', 'true')}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 uppercase text-xs tracking-wider font-mono">Overdue</p>
                <p className={`text-3xl font-bold mt-1 ${overdueTasks.length > 0 ? 'text-red-400' : 'text-slate-400'}`}>
                  {overdueTasks.length}
                </p>
              </div>
              <div className={`w-12 h-12 rounded-sm flex items-center justify-center ${overdueTasks.length > 0 ? 'bg-red-500/20' : 'bg-slate-700'}`}>
                <AlertTriangle className={`w-6 h-6 ${overdueTasks.length > 0 ? 'text-red-400' : 'text-slate-400'}`} />
              </div>
            </div>
          </div>
        </div>

        {/* Charts Row 1 */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Task Status Pie Chart */}
          <div className="card-industrial" data-testid="chart-status">
            <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight text-white flex items-center gap-2 mb-4">
              <PieChartIcon className="w-5 h-5 text-amber-500" />
              Work Order Status Distribution
            </h2>
            {statusData.length > 0 ? (
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={statusData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      labelLine={{ stroke: '#52525B' }}
                      style={{ cursor: 'pointer' }}
                    >
                      {statusData.map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={entry.color}
                          onClick={() => handleFilterNavigation('status', entry.name)}
                          style={{ cursor: 'pointer' }}
                        />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-slate-400">
                No tasks to display
              </div>
            )}
            <div className="flex flex-wrap justify-center gap-4 mt-4">
              {statusData.map((item, index) => (
                <div 
                  key={index} 
                  className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity px-2 py-1 rounded hover:bg-slate-700/50"
                  onClick={() => handleFilterNavigation('status', item.name)}
                >
                  <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: item.color }} />
                  <span className="text-slate-400 text-sm hover:text-white">{item.name}: {item.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Priority Distribution */}
          <div className="card-industrial" data-testid="chart-priority">
            <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight text-white flex items-center gap-2 mb-4">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Priority Distribution
            </h2>
            {priorityData.length > 0 ? (
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={priorityData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      labelLine={{ stroke: '#52525B' }}
                      style={{ cursor: 'pointer' }}
                    >
                      {priorityData.map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={entry.color}
                          onClick={() => handleFilterNavigation('priority', entry.name)}
                          style={{ cursor: 'pointer' }}
                        />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-slate-400">
                No tasks to display
              </div>
            )}
            <div className="flex flex-wrap justify-center gap-4 mt-4">
              {priorityData.map((item, index) => (
                <div 
                  key={index} 
                  className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity px-2 py-1 rounded hover:bg-slate-700/50"
                  onClick={() => handleFilterNavigation('priority', item.name)}
                >
                  <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: item.color }} />
                  <span className="text-slate-400 text-sm hover:text-white">{item.name}: {item.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Tasks Over Time */}
        <div className="card-industrial" data-testid="chart-timeline">
          <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight text-white flex items-center gap-2 mb-4">
            <TrendingUp className="w-5 h-5 text-amber-500" />
            Work Orders Over Time (Last 7 Days)
          </h2>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={last7Days}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                <XAxis dataKey="date" stroke="#52525B" fontSize={12} />
                <YAxis stroke="#52525B" fontSize={12} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Area 
                  type="monotone" 
                  dataKey="created" 
                  name="Created"
                  stroke="#3B82F6" 
                  fill="#3B82F6" 
                  fillOpacity={0.3}
                />
                <Area 
                  type="monotone" 
                  dataKey="completed" 
                  name="Completed"
                  stroke="#22C55E" 
                  fill="#22C55E" 
                  fillOpacity={0.3}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Tasks by Category */}
        <div className="card-industrial" data-testid="chart-categories">
          <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight text-white flex items-center gap-2 mb-4">
            <BarChart3 className="w-5 h-5 text-amber-500" />
            Work Orders by Category
          </h2>
          {categoryData.length > 0 ? (
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart 
                  data={categoryData} 
                  layout="vertical"
                  onClick={(data) => {
                    if (data && data.activeLabel) {
                      handleFilterNavigation('category', data.activeLabel);
                    }
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                  <XAxis type="number" stroke="#52525B" fontSize={12} />
                  <YAxis type="category" dataKey="name" stroke="#52525B" fontSize={12} width={100} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar dataKey="completed" name="Completed" fill="#22C55E" stackId="stack" style={{ cursor: 'pointer' }} />
                  <Bar dataKey="pending" name="Pending" fill="#EAB308" stackId="stack" style={{ cursor: 'pointer' }} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-slate-400">
              No categorized tasks yet
            </div>
          )}
        </div>

        {/* Tasks by Assignee */}
        {assigneeChartData.length > 0 && (
          <div className="card-industrial" data-testid="chart-assignees">
            <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight text-white flex items-center gap-2 mb-4">
              <Users className="w-5 h-5 text-amber-500" />
              Work Orders by Assignee
            </h2>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart 
                  data={assigneeChartData}
                  onClick={(data) => {
                    if (data && data.activeLabel) {
                      handleFilterNavigation('assignee', data.activeLabel);
                    }
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                  <XAxis dataKey="name" stroke="#52525B" fontSize={12} />
                  <YAxis stroke="#52525B" fontSize={12} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar dataKey="completed" name="Completed" fill="#22C55E" style={{ cursor: 'pointer' }} />
                  <Bar dataKey="pending" name="Pending" fill="#EAB308" style={{ cursor: 'pointer' }} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Milestone Progress */}
        <div className="grid md:grid-cols-2 gap-6">
          <div 
            className="card-industrial cursor-pointer hover:border-amber-500/50 transition-colors" 
            data-testid="milestone-progress"
            onClick={() => navigate('/tasks?has_milestones=true')}
          >
            <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight text-white flex items-center gap-2 mb-4">
              <CheckCircle2 className="w-5 h-5 text-amber-500" />
              Milestone Progress
            </h2>
            <div className="flex items-center justify-between mb-4">
              <span className="text-slate-400">Overall Milestone Completion</span>
              <span className="text-2xl font-bold text-white">{milestoneProgress}%</span>
            </div>
            <div className="h-4 bg-slate-700 rounded-full overflow-hidden mb-4">
              <div 
                className="h-full bg-gradient-to-r from-amber-500 to-yellow-500 transition-all duration-500"
                style={{ width: `${milestoneProgress}%` }}
              />
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-400">{completedMilestones} completed</span>
              <span className="text-amber-400 hover:underline">{totalMilestones - completedMilestones} remaining →</span>
            </div>
          </div>

          {/* Overdue Tasks List */}
          <div className="card-industrial" data-testid="overdue-list">
            <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight text-white flex items-center gap-2 mb-4">
              <Calendar className="w-5 h-5 text-amber-500" />
              Overdue Work Orders
            </h2>
            {overdueTasks.length > 0 ? (
              <div className="space-y-3 max-h-[250px] overflow-y-auto">
                {overdueTasks.slice(0, 5).map((task) => (
                  <div 
                    key={task.id}
                    className="p-3 bg-red-500/10 border border-red-500/30 rounded-sm cursor-pointer hover:bg-red-500/20 hover:border-red-500/50 transition-colors"
                    onClick={() => navigate(`/tasks/${task.id}`)}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-white font-medium"><TranslatableSpan text={task.title} /></p>
                        <p className="text-red-400 text-sm mt-1">
                          Due: {formatDate(task.deadline)}
                        </p>
                      </div>
                      <span className={`priority-indicator ${task.priority}`}></span>
                    </div>
                  </div>
                ))}
                {overdueTasks.length > 5 && (
                  <p 
                    className="text-amber-400 text-sm text-center cursor-pointer hover:underline"
                    onClick={() => navigate('/tasks?overdue=true')}
                  >
                    +{overdueTasks.length - 5} more overdue tasks →
                  </p>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-slate-400">
                <CheckCircle2 className="w-12 h-12 text-green-500 mb-3" />
                <p>No overdue tasks!</p>
                <p className="text-sm text-slate-500">Great job staying on schedule</p>
              </div>
            )}
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div 
            className="card-industrial text-center cursor-pointer hover:border-red-500/50 transition-colors"
            onClick={() => handleFilterNavigation('priority', 'Critical')}
          >
            <p className="text-slate-400 uppercase text-xs tracking-wider font-mono mb-2">Critical Work Orders</p>
            <p className="text-3xl font-bold text-red-400">
              {tasks.filter(t => t.priority === 'critical' && t.status !== 'completed').length}
            </p>
          </div>
          <div 
            className="card-industrial text-center cursor-pointer hover:border-amber-500/50 transition-colors"
            onClick={() => handleFilterNavigation('priority', 'High')}
          >
            <p className="text-slate-400 uppercase text-xs tracking-wider font-mono mb-2">High Priority</p>
            <p className="text-3xl font-bold text-amber-400">
              {tasks.filter(t => t.priority === 'high' && t.status !== 'completed').length}
            </p>
          </div>
          <div 
            className="card-industrial text-center cursor-pointer hover:border-blue-500/50 transition-colors"
            onClick={() => handleFilterNavigation('unassigned', 'true')}
          >
            <p className="text-slate-400 uppercase text-xs tracking-wider font-mono mb-2">Unassigned</p>
            <p className="text-3xl font-bold text-blue-400">
              {tasks.filter(t => !t.assigned_to && t.status !== 'completed').length}
            </p>
          </div>
          <div className="card-industrial text-center">
            <p className="text-slate-400 uppercase text-xs tracking-wider font-mono mb-2">Avg. Milestones</p>
            <p className="text-3xl font-bold text-purple-400">
              {totalTasks > 0 ? (totalMilestones / totalTasks).toFixed(1) : 0}
            </p>
          </div>
        </div>

        {/* Inventory Section */}
        <div className="space-y-6">
          <h2 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-tight text-white flex items-center gap-2">
            <Package className="w-6 h-6 text-amber-500" />
            Inventory Overview
          </h2>
          
          {/* Inventory Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="card-industrial" data-testid="inventory-total">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 uppercase text-xs tracking-wider font-mono">Total Items</p>
                  <p className="text-3xl font-bold text-white mt-1">{inventory.length}</p>
                </div>
                <div className="w-12 h-12 bg-slate-700 rounded-sm flex items-center justify-center">
                  <Package className="w-6 h-6 text-slate-400" />
                </div>
              </div>
            </div>
            
            <div className="card-industrial" data-testid="inventory-low-stock">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 uppercase text-xs tracking-wider font-mono">Low Stock</p>
                  <p className={`text-3xl font-bold mt-1 ${lowStockItems.length > 0 ? 'text-red-400' : 'text-green-400'}`}>
                    {lowStockItems.length}
                  </p>
                </div>
                <div className={`w-12 h-12 rounded-sm flex items-center justify-center ${lowStockItems.length > 0 ? 'bg-red-500/20' : 'bg-green-500/20'}`}>
                  <AlertTriangle className={`w-6 h-6 ${lowStockItems.length > 0 ? 'text-red-400' : 'text-green-400'}`} />
                </div>
              </div>
            </div>
            
            <div className="card-industrial" data-testid="inventory-value">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 uppercase text-xs tracking-wider font-mono">Total Value</p>
                  <p className="text-3xl font-bold text-green-400 mt-1">${totalInventoryValue.toFixed(2)}</p>
                </div>
                <div className="w-12 h-12 bg-green-500/20 rounded-sm flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-green-400" />
                </div>
              </div>
            </div>
            
            <div className="card-industrial" data-testid="parts-cost">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 uppercase text-xs tracking-wider font-mono">Parts Used</p>
                  <p className="text-3xl font-bold text-amber-400 mt-1">${totalPartsCost.toFixed(2)}</p>
                </div>
                <div className="w-12 h-12 bg-amber-500/20 rounded-sm flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-amber-400" />
                </div>
              </div>
            </div>
          </div>
          
          {/* Low Stock Items */}
          {lowStockItems.length > 0 && (
            <div className="card-industrial border-red-500/30">
              <h3 className="font-['Barlow_Condensed'] text-lg font-bold uppercase tracking-tight text-white flex items-center gap-2 mb-4">
                <AlertTriangle className="w-5 h-5 text-red-400" />
                Low Stock Alert
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left text-slate-400 uppercase text-xs font-mono py-2">Item</th>
                      <th className="text-left text-slate-400 uppercase text-xs font-mono py-2">SKU</th>
                      <th className="text-right text-slate-400 uppercase text-xs font-mono py-2">Current</th>
                      <th className="text-right text-slate-400 uppercase text-xs font-mono py-2">Min Required</th>
                      <th className="text-right text-slate-400 uppercase text-xs font-mono py-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lowStockItems.map(item => (
                      <tr key={item.id} className="border-b border-slate-700/50">
                        <td className="py-3 text-white font-medium">{item.name}</td>
                        <td className="py-3 text-slate-400">{item.sku || 'N/A'}</td>
                        <td className="py-3 text-right text-red-400 font-bold">{item.quantity}</td>
                        <td className="py-3 text-right text-slate-400">{item.min_quantity}</td>
                        <td className="py-3 text-right">
                          <span className="px-2 py-1 text-xs font-bold uppercase rounded bg-red-500/20 text-red-400">
                            {item.quantity === 0 ? 'OUT OF STOCK' : 'LOW STOCK'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          
          {/* Inventory List */}
          <div className="card-industrial">
            <h3 className="font-['Barlow_Condensed'] text-lg font-bold uppercase tracking-tight text-white flex items-center gap-2 mb-4">
              <Package className="w-5 h-5 text-amber-400" />
              Inventory Items ({inventory.length})
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left text-slate-400 uppercase text-xs font-mono py-2">Item</th>
                    <th className="text-left text-slate-400 uppercase text-xs font-mono py-2">SKU</th>
                    <th className="text-right text-slate-400 uppercase text-xs font-mono py-2">Qty</th>
                    <th className="text-right text-slate-400 uppercase text-xs font-mono py-2">Unit Cost</th>
                    <th className="text-right text-slate-400 uppercase text-xs font-mono py-2">Total Value</th>
                  </tr>
                </thead>
                <tbody>
                  {inventory.map(item => (
                    <tr key={item.id} className={`border-b border-slate-700/50 ${item.quantity <= item.min_quantity ? 'bg-red-500/10' : ''}`}>
                      <td className="py-3 text-white font-medium">{item.name}</td>
                      <td className="py-3 text-slate-400">{item.sku || 'N/A'}</td>
                      <td className={`py-3 text-right font-bold ${item.quantity <= item.min_quantity ? 'text-red-400' : 'text-white'}`}>
                        {item.quantity} {item.unit}
                      </td>
                      <td className="py-3 text-right text-slate-400">${(item.cost_per_unit || 0).toFixed(2)}</td>
                      <td className="py-3 text-right text-green-400 font-medium">${(item.quantity * (item.cost_per_unit || 0)).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-slate-600">
                    <td colSpan="2" className="py-3 text-slate-400 font-bold uppercase">Total</td>
                    <td className="py-3 text-right font-bold text-white">{totalUnits} units</td>
                    <td className="py-3"></td>
                    <td className="py-3 text-right font-bold text-green-400 text-lg">${totalInventoryValue.toFixed(2)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ReportsPage;
